import { DEFAULT_RECT, Rect } from './dom'
import { Placement, PlacementNS } from './types'

interface Components {
  align: Placement
  edge: Placement
}

type Validator = (container: Rect, target: Rect, overlay: Rect) => boolean
type Updater = (target: Rect, overlay: Rect) => object
type ValidatorMap = { [s: string]: Validator }
type UpdaterMap = { [s: string]: Updater }
type PlacementMap = { [s: string]: Placement }

interface ComponentUpdater {
  validator: ValidatorMap
  updater: UpdaterMap
}

interface Config {
  container: Rect
  target: Rect
  overlay: Rect
  canFlip?: boolean
  placement: Placement
}

// UTILS

const getComponents: (placement: Placement) => Components = placement => {
  const [primary, secondary] = placement.split('-')
  const edge = primary as Placement
  const align = (secondary as Placement) || PlacementNS.center
  return { edge, align }
}

const fromComponents: (components: Components) => Placement = ({
  align,
  edge,
}) => (align === PlacementNS.center ? edge : (`${edge}-${align}` as Placement))

const getContainer: () => Rect = () => ({
  top: 0,
  left: 0,
  width: window.innerWidth,
  height: window.innerHeight,
  bottom: window.innerWidth,
  right: window.innerWidth,
})

const flip: (current: Placement) => Placement = current => {
  const opposite: PlacementMap = {
    top: PlacementNS.bottom,
    bottom: PlacementNS.top,
    left: PlacementNS.right,
    right: PlacementNS.left,
    center: PlacementNS.center,
  }
  return opposite[current]
}

// GENERIC POSITION UPDATER (Edge, Align)
// Flip updater if validator fails and opposite passes
const getPositionComponent: (a: ComponentUpdater) => (c: Config) => any = ({
  validator,
  updater,
}) => ({ container, target, overlay, placement: current, canFlip = true }) => {
  const opposite = flip(current)
  const shouldFlip =
    !validator[current](container, target, overlay) &&
    validator[opposite](container, target, overlay)
  const placement = canFlip && shouldFlip ? opposite : current

  return {
    placement,
    ...updater[placement](target, overlay),
  }
}

// PRIMARY
const Edge: ComponentUpdater = {
  updater: {
    left: (target, overlay) => ({
      left: target.left - overlay.width,
    }),
    right: (target, overlay) => ({ left: target.right }),
    top: (target, overlay) => ({
      top: target.top - overlay.height,
    }),
    bottom: (target, overlay) => ({ top: target.bottom }),
  },
  validator: {
    top: (container, target, overlay) =>
      target.top - container.top >= overlay.height,
    left: (container, target, overlay) =>
      target.left - container.left >= overlay.width,
    bottom: (container, target, overlay) =>
      container.height - target.bottom >= overlay.height,
    right: (container, target, overlay) =>
      container.width - target.right >= overlay.width,
  },
}

// SECONDARY
export const Alignment: ComponentUpdater = {
  updater: {
    left: (target, overlay) => ({ left: target.left }),
    right: (target, overlay) => ({ left: target.right - overlay.width }),
    top: (target, overlay) => ({ top: target.top }),
    bottom: (target, overlay) => ({ top: target.bottom - overlay.height }),
    center: (target, overlay) => ({
      left: target.left + (target.width - overlay.width) / 2,
      top: target.top + (target.height - overlay.height) / 2,
    }),
  },
  validator: {
    top: (container, target, overlay) =>
      container.height - target.top >= overlay.height,
    right: (container, target, overlay) =>
      target.right - container.left >= overlay.width,
    left: (container, target, overlay) =>
      container.width - target.left >= overlay.width,
    bottom: (container, target, overlay) =>
      target.bottom - container.top >= overlay.height,
    center: () => true,
  },
}

export const getAlignmentPosition = getPositionComponent(Alignment)

export const getEdgePosition = getPositionComponent(Edge)

export const padding = (pad: number = 0, shape: Rect) => ({
  ...shape,
  width: shape.width + pad * 2,
  height: shape.height + pad * 2,
  top: shape.top - pad,
  bottom: shape.bottom + pad,
  left: shape.left - pad,
  right: shape.right + pad,
})

export const getAnchorPosition = (
  placement: Placement,
  pad: number = 0,
  container: Rect = getContainer(),
  target: Rect,
  overlay: Rect,
  canFlip: boolean = true
) => {
  if (!target || !overlay) {
    return DEFAULT_RECT
  }

  let components = getComponents(placement)

  const align = getAlignmentPosition({
    canFlip,
    container,
    target,
    overlay,
    placement: components.align,
  })

  const edge = getEdgePosition({
    canFlip,
    container,
    target: padding(pad, target),
    overlay,
    placement: components.edge,
  })

  return {
    ...align,
    ...edge,
    placement: fromComponents({ align: align.placement, edge: edge.placement }),
    minWidth: Math.max(target.width, overlay.width),
  }
}
